/**
 * Tiny dependency-free markdown renderer for chat responses.
 * Supports: headings, bold, italic, inline code, lists, tables, blockquotes, links, paragraphs.
 * Designed for streaming — pure function, returns a React element.
 */
import React from "react";

function escapeHtml(s) {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function renderInline(text) {
  let s = escapeHtml(text);
  // Bold
  s = s.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  // Italic
  s = s.replace(/(^|[^*])\*(?!\s)([^*]+?)\*(?!\*)/g, "$1<em>$2</em>");
  s = s.replace(/_([^_]+?)_/g, "<em>$1</em>");
  // Inline code
  s = s.replace(/`([^`]+?)`/g, "<code>$1</code>");
  // Links [text](url)
  s = s.replace(/\[(.+?)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" target="_blank" rel="noreferrer">$1</a>');
  return s;
}

function parseTable(lines, start) {
  // detect header line + separator line + body lines
  if (start + 1 >= lines.length) return null;
  const header = lines[start];
  const sep = lines[start + 1];
  if (!/^\s*\|.*\|\s*$/.test(header)) return null;
  if (!/^\s*\|?[\s:|-]+\|?\s*$/.test(sep) || !sep.includes("-")) return null;

  const splitRow = (l) =>
    l.trim().replace(/^\|/, "").replace(/\|$/, "").split("|").map((c) => c.trim());

  const headers = splitRow(header);
  let i = start + 2;
  const rows = [];
  while (i < lines.length && /^\s*\|.*\|\s*$/.test(lines[i])) {
    rows.push(splitRow(lines[i]));
    i++;
  }
  return { headers, rows, end: i };
}

export default function Markdown({ text = "" }) {
  const lines = text.split("\n");
  const blocks = [];
  let i = 0;
  let buf = [];
  const flushParagraph = () => {
    if (buf.length) {
      blocks.push({ type: "p", content: buf.join(" ") });
      buf = [];
    }
  };

  while (i < lines.length) {
    const line = lines[i];

    // Blank line
    if (/^\s*$/.test(line)) {
      flushParagraph();
      i++;
      continue;
    }

    // Heading
    const heading = line.match(/^(#{1,3})\s+(.*)$/);
    if (heading) {
      flushParagraph();
      blocks.push({ type: `h${heading[1].length}`, content: heading[2] });
      i++;
      continue;
    }

    // Blockquote
    if (/^\s*>\s?/.test(line)) {
      flushParagraph();
      const qLines = [];
      while (i < lines.length && /^\s*>\s?/.test(lines[i])) {
        qLines.push(lines[i].replace(/^\s*>\s?/, ""));
        i++;
      }
      blocks.push({ type: "blockquote", content: qLines.join(" ") });
      continue;
    }

    // Unordered list
    if (/^\s*[-*]\s+/.test(line)) {
      flushParagraph();
      const items = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*]\s+/, ""));
        i++;
      }
      blocks.push({ type: "ul", items });
      continue;
    }

    // Ordered list
    if (/^\s*\d+\.\s+/.test(line)) {
      flushParagraph();
      const items = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*\d+\.\s+/, ""));
        i++;
      }
      blocks.push({ type: "ol", items });
      continue;
    }

    // Table
    if (/^\s*\|.*\|\s*$/.test(line)) {
      const tbl = parseTable(lines, i);
      if (tbl) {
        flushParagraph();
        blocks.push({ type: "table", headers: tbl.headers, rows: tbl.rows });
        i = tbl.end;
        continue;
      }
    }

    // Paragraph buffer
    buf.push(line.trim());
    i++;
  }
  flushParagraph();

  return (
    <div className="ig-prose text-[15px] text-foreground">
      {blocks.map((b, idx) => {
        if (b.type === "p") return <p key={idx} dangerouslySetInnerHTML={{ __html: renderInline(b.content) }} />;
        if (b.type === "h1") return <h2 key={idx} dangerouslySetInnerHTML={{ __html: renderInline(b.content) }} />;
        if (b.type === "h2") return <h3 key={idx} dangerouslySetInnerHTML={{ __html: renderInline(b.content) }} />;
        if (b.type === "h3") return <h4 key={idx} className="font-semibold mt-3 mb-1" dangerouslySetInnerHTML={{ __html: renderInline(b.content) }} />;
        if (b.type === "blockquote") return <blockquote key={idx} dangerouslySetInnerHTML={{ __html: renderInline(b.content) }} />;
        if (b.type === "ul") return (
          <ul key={idx}>{b.items.map((it, j) => <li key={j} dangerouslySetInnerHTML={{ __html: renderInline(it) }} />)}</ul>
        );
        if (b.type === "ol") return (
          <ol key={idx}>{b.items.map((it, j) => <li key={j} dangerouslySetInnerHTML={{ __html: renderInline(it) }} />)}</ol>
        );
        if (b.type === "table") return (
          <div key={idx} className="overflow-x-auto -mx-1 my-3">
            <table>
              <thead><tr>{b.headers.map((h, j) => <th key={j} dangerouslySetInnerHTML={{ __html: renderInline(h) }} />)}</tr></thead>
              <tbody>{b.rows.map((row, j) => (
                <tr key={j}>{row.map((c, k) => <td key={k} dangerouslySetInnerHTML={{ __html: renderInline(c) }} />)}</tr>
              ))}</tbody>
            </table>
          </div>
        );
        return null;
      })}
    </div>
  );
}
